
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import { edgeFragmentShaderString } from '@/shaders/edgeFragment'
import References from '@/singletons/references'
import edgeSettings from '@/settings/edge-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselEdge',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const texDefaultN = computed(() => store.getters.texDefaultN)
    const texBody = computed(() => store.getters.texBody)
    const texBodyN = computed(() => store.getters.texBodyN)
    const configEdgeId = computed(() => store.getters.configEdgeId)
    const colorpickerEdge = computed(() => store.getters.colorpickerEdge)
    const colorpickerEdgeGlosiness = computed(() => store.getters.colorpickerEdgeGlosiness)
    const colorpickerEdgeMetalness = computed(() => store.getters.colorpickerEdgeMetalness)

    const isRenderer = inject('isRenderer') as boolean
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      try {
        initTextureLoadService()
        let mat = materialCreateService.createMaterial(edgeFragmentShaderString, true, true, isRenderer)
        mat.uniforms.bodyTexture = { value: null };
        mat.uniforms.hookerColor = { value: materialCreateService.createColor(0xFFFFFF) };
        mat.uniforms.edgeColor = { value: materialCreateService.createColor(0x00FF00) };
        mat.uniforms.edgeAlpha = { value: 0 };
        mat.uniforms.itoTexture = { value: null };
        mat.uniforms.itoColor = { value: materialCreateService.createColor(0xFFFFFF) };
        mat.uniforms.normalScale.value = materialCreateService.getNormalMapScale()
        mat.uniforms.roughness.value = materialCreateService.getRoughness()
        mat.uniforms.metalness.value = materialCreateService.getMetalness()
        mat.uniforms.hookerTexture = { value: null };
        textureLoadService.loadImage('default/hooker', (tex: THREE.Texture | null) => {
          mat.uniforms.hookerTexture.value = tex
        })
        References.instance.setEdgeMaterial(mat)
      }
      catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }
    )

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => isBagLoaded.value, (_) => { References.instance.getBagReferences().edge.material = References.instance.getEdgeMaterial() })
    // prettier-ignore
    watch(() => texDefaultN.value, (_) => { References.instance.getEdgeMaterial().uniforms.normalMap.value = References.instance.getBagTextures().texDefaultN })
    // prettier-ignore
    watch(() => texBody.value, (_) => { References.instance.getEdgeMaterial().uniforms.bodyTexture.value = References.instance.getBagTextures().texBody })
    // prettier-ignore
    watch(() => texBodyN.value, (_) => { References.instance.getEdgeMaterial().uniforms.normalMap.value = References.instance.getBagTextures().texBodyN })
    // prettier-ignore
    watch(() => configEdgeId.value, (_) => { updateOption() })
    // prettier-ignore
    watch(() => colorpickerEdge.value, (_) => { updateColorpicker() })
    // prettier-ignore
    watch(() => colorpickerEdgeGlosiness.value, (_) => { updateColorSlider() })
    // prettier-ignore
    watch(() => colorpickerEdgeMetalness.value, (_) => { updateColorSlider() })

    function updateOption() {
      try {
        initTextureLoadService()
        if (configEdgeId.value) {
          setEdgeColor(edgeSettings.edgeColors.find((x: any) => x.id == configEdgeId.value))
        } else {
          setEdgeColor(edgeSettings.edgeColors.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setEdgeColor(obj: any) {
      if (obj) {
        References.instance.getEdgeMaterial().uniforms.edgeColor.value = materialCreateService.createColor(obj.value)
        References.instance.getEdgeMaterial().uniforms.edgeAlpha.value = 1
        References.instance.getRibbonMaterial().uniforms.edgeColor.value = materialCreateService.createColor(obj.value)
        References.instance.getRibbonMaterial().uniforms.edgeAlpha.value = 1
      } else {
        References.instance.getEdgeMaterial().uniforms.edgeAlpha.value = 0
        References.instance.getRibbonMaterial().uniforms.edgeAlpha.value = 0
      }
      const glosiness = obj && obj.glosiness ? obj.glosiness : edgeSettings.defaultGlosiness
      const metalness = obj && obj.metalness ? obj.metalness : edgeSettings.defaultMetalness
      const hookerColor = obj ? determineHookerColor(obj.id, obj.value) : null
      References.instance.getEdgeMaterial().uniforms.roughness.value = (100 - glosiness) / 100
      References.instance.getEdgeMaterial().uniforms.metalness.value = metalness / 100
      References.instance.getRibbonMaterial().uniforms.roughness.value = (100 - glosiness) / 100
      References.instance.getRibbonMaterial().uniforms.metalness.value = metalness / 100
      References.instance.getEdgeMaterial().uniforms.hookerColor.value = materialCreateService.createColor(hookerColor)
    }

    function determineHookerColor(id: string, edgeColor: THREE.Color) {
      let hookerException = edgeSettings.hookerColorExceptions.find((obj) => obj.exceptColorId == id)
      if (hookerException) {
        return edgeSettings.edgeColors.find((obj) => obj.id == hookerException!.replaceColorId)!.value
      }
      return edgeColor
    }

    function updateColorpicker() {
      const color = materialCreateService.createColor(colorpickerEdge.value)
      References.instance.getEdgeMaterial().uniforms.edgeColor.value = color
      References.instance.getEdgeMaterial().uniforms.edgeAlpha.value = 1
      References.instance.getEdgeMaterial().uniforms.hookerColor.value = color
      References.instance.getRibbonMaterial().uniforms.edgeColor.value = color
      References.instance.getRibbonMaterial().uniforms.edgeAlpha.value = 1
    }

    function updateColorSlider() {
      // @ts-ignore
      const glosiness = colorpickerEdgeGlosiness.value ? parseInt(colorpickerEdgeGlosiness.value) : edgeSettings.defaultGlosiness
      // @ts-ignore
      const metalness = colorpickerEdgeMetalness.value ? parseInt(colorpickerEdgeMetalness.value) : edgeSettings.defaultMetalness

      References.instance.getEdgeMaterial().uniforms.roughness.value = (100 - glosiness) / 100
      References.instance.getEdgeMaterial().uniforms.metalness.value = metalness / 100
      References.instance.getRibbonMaterial().uniforms.roughness.value = (100 - glosiness) / 100
      References.instance.getRibbonMaterial().uniforms.metalness.value = metalness / 100
    }

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isBagLoaded,
      isSceneLoaded,
      texBody,
      configEdgeId,
      colorpickerEdge
    }
  }
})
